/**
 * Utility functions for employee pages management
 */

import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  collection,
  updateDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db } from "../config/firebase";
import CryptoJS from "crypto-js";

/**
 * Hash a string using Web Crypto API (SHA-256) with fallback to crypto-js for HTTP contexts
 * @param {string} str - String to hash
 * @returns {Promise<string>} - Hashed string (hex)
 */
export const hashString = async (str) => {
  // Normalize the string before hashing (remove whitespace and non-numeric characters)
  const normalizedStr = String(str)
    .replace(/\s+/g, "")
    .replace(/[^\d]/g, "")
    .trim();

  if (!normalizedStr) {
    console.warn(
      "Attempted to hash an empty or non-numeric string after normalization."
    );
    return null;
  }

  // Check if crypto.subtle is available (requires HTTPS or localhost)
  if (window.isSecureContext || window.location.protocol === "https:") {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(normalizedStr);
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      return hashHex;
    } catch (e) {
      console.error(
        "Crypto.subtle hashing failed, falling back to CryptoJS:",
        e
      );
      // Fallback to CryptoJS if subtle fails in a secure context for some reason
      return CryptoJS.SHA256(normalizedStr).toString();
    }
  } else {
    // Fallback for non-secure contexts (HTTP)
    return CryptoJS.SHA256(normalizedStr).toString();
  }
};

/**
 * New employee page document IDs: 128-bit random hex (harder to guess than legacy 9-char suffix).
 * Existing pages keep their current Firestore document id; lookup is by personalNumber first.
 */
const createSecurePageId = () => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Get or create an employee page
 * @param {string} personnelId - Employee's personnel document ID (e.g., "PERSONNEL_1760821452141_j4314zcjh")
 * @param {string} personalNumber - Employee's personal number (for lookup and password)
 * @param {string} createdByEmail - Email of admin who creates the page
 * @returns {Promise<string>} - Firestore document id for employeePages (legacy short or new secure id)
 */
export const getOrCreatePageId = async (
  personnelId,
  personalNumber,
  createdByEmail
) => {
  try {
    const pagesQuery = query(
      collection(db, "employeePages"),
      where("personnelId", "==", personalNumber)
    );
    const snapshot = await getDocs(pagesQuery);

    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }

    const pageId = createSecurePageId();
    const passwordHash = await hashString(personalNumber);

    await setDoc(doc(db, "employeePages", pageId), {
      pageId,
      personnelId: personalNumber, // Keep personalNumber for lookup
      personnelDocumentId: personnelId, // Store full document ID for reference
      passwordHash,
      isActive: true,
      isPublic: false,
      lastTransaction: null,
      totalTransactionsCount: 0,
      lastTransactionAt: null,
      lastViewedAt: null,
      createdAt: serverTimestamp(),
      createdBy: createdByEmail || "system",
    });

    return pageId;
  } catch (error) {
    console.error("Error getting or creating pageId:", error);
    throw error;
  }
};

/**
 * Check if page creation is allowed (for future admin control)
 * @returns {Promise<boolean>} - true if allowed, false otherwise
 */
export const checkIfPageCreationAllowed = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, "settings", "employeePages"));
    const settings = settingsDoc.data();

    // If settings don't exist, default to allowed
    if (!settings) return true;

    return settings.allowNewPages !== false;
  } catch (error) {
    console.error("Error checking page creation setting:", error);
    return true; // Default to allowed if error
  }
};

/**
 * Update employee page with new transaction
 * @param {string} pageId - Page ID
 * @param {Object} transactionData - Transaction data to store
 * @returns {Promise<void>}
 */
export const updateEmployeePage = async (pageId, transactionData) => {
  try {
    await updateDoc(doc(db, "employeePages", pageId), {
      lastTransaction: transactionData,
      totalTransactionsCount: increment(1),
      lastTransactionAt: serverTimestamp(),
      lastUpdated: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error updating employee page:", error);
    throw error;
  }
};

/**
 * Mark employee page as viewed
 * @param {string} pageId - Page ID
 * @returns {Promise<void>}
 */
export const markPageAsViewed = async (pageId) => {
  try {
    await updateDoc(doc(db, "employeePages", pageId), {
      lastViewedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error marking page as viewed:", error);
    throw error;
  }
};
